import React, { Component } from "react";
import PropTypes from "prop-types";
import Cover from "./cover";
import { withTheme } from "styled-components";
import { Card } from "@vitalsource/vstui";
import classnames from "classnames";
import { TextEllipsis } from "../../../components/text-ellipsis";
import { OffScreenComponent } from "../../../components/mixins";
import styled from "styled-components";
import { t } from "../../../core/translate";
import {
  BookCoverOuter,
  PrimaryTransparentButton,
  PrimaryWhiteButton,
  RoundButton,
  ButtonWrapper,
  PrimaryButtons,
  SecondaryButtons,
  Badges,
  Title,
  SkeletonCoverOuter,
  BookCoverIconOverlay,
  computeImageHeight,
  Badge,
  DurationMessage,
  AvailabilityMessage
} from "./BookIcons";
import { cloud } from "@vitalsource/vstui/colors";

export const BASE_CARD_WIDTH = 230;
export const BASE_CARD_HEIGHT = 330;

const PrependText = styled.span`
  font-weight: 300;
  text-transform: uppercase;
  color: ${cloud.getShade(9)};
  font-size: 15px;
`;

const HeroCard = styled(Card)`
  box-shadow: 0 5px 15px 4px rgba(0, 0, 0, 0.2);
`;

class Book extends Component {
  constructor(props) {
    super(props);
    this.state = {
      clickable: true,
      focus: false,
      fullRender: !this.props.skeleton
    };

    this._onFocus = this._onFocus.bind(this);
    this._onBlur = this._onBlur.bind(this);
    this.clickCover = this.clickCover.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (!nextProps.skeleton && !this.state.fullRender) {
      this.setState({
        fullRender: true
      });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      this.state !== nextState ||
      this.props.width !== nextProps.width ||
      this.props.availabilityMessage !== nextProps.availabilityMessage ||
      this.props.primary !== nextProps.primary
    );
  }

  buttonClick(button, e) {
    if (this.state.clickable) {
      button.onClick(e);
    }
  }

  buttonTouchStart(e) {
    e.stopPropagation();
  }

  _onFocus() {
    this.setState({ focus: true });
  }

  _onBlur() {
    this.setState({ focus: false });
  }

  clickCover() {
    if (this.state.clickable) {
      const { onClick } = this.props;
      if (onClick) {
        onClick();
      }
    }
  }

  renderButtons() {
    const primary = this.props.primary.map(button => {
      switch (button.type) {
        case "white":
          return (
            <PrimaryWhiteButton
              key={button.label}
              color="white"
              onClick={e => {
                e.stopPropagation();
                this.buttonClick(button, e);
              }}
              colorBehindButton={cloud.getShade(9)}
              fullWidth
              onFocus={this._onFocus}
              onBlur={this._onBlur}
              onTouchStart={this.buttonTouchStart}
              cardHeight={this.props.height}
              aria-label={button.ariaLabel}
            >
              {button.label}
            </PrimaryWhiteButton>
          );
        default:
          return (
            <PrimaryTransparentButton
              href={button.href}
              key={button.label}
              fullWidth
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                this.buttonClick(button, e);
              }}
              onFocus={this._onFocus}
              onBlur={this._onBlur}
              onTouchStart={this.buttonTouchStart}
              cardHeight={this.props.height}
              aria-label={button.ariaLabel}
            >
              {button.label}
            </PrimaryTransparentButton>
          );
      }
    });

    const secondary = this.props.secondary.map(button => {
      return (
        <RoundButton
          key={button.label}
          label={button.label}
          iconStart={<button.icon />}
          onClick={e => {
            e.stopPropagation();
            this.buttonClick(button, e);
          }}
          onFocus={this._onFocus}
          onBlur={this._onBlur}
          onTouchStart={this.buttonTouchStart}
          cardHeight={this.props.height}
          fullHeight
          size="small"
          favorite={button.favorite}
          tooltipPlacement="bottom"
        />
      );
    });
    const availability = (
      <AvailabilityMessage cardHeight={this.props.height}>
        <div>{this.props.availabilityMessage.icon}</div>
        {this.props.availabilityMessage.label}
      </AvailabilityMessage>
    );

    const { Icon, label } = this.props.durationMessage;
    const duration = (
      <DurationMessage cardHeight={this.props.height}>
        {Icon && <Icon />}
        {label}
      </DurationMessage>
    );
    const badges = this.props.badges.map(button => {
      return (
        <Badge
          key={button.label}
          onClick={e => {
            e.stopPropagation();
            button.onClick();
          }}
          onFocus={this._onFocus}
          onBlur={this._onBlur}
          label={button.label}
          iconStart={button.icon ? <button.icon /> : ''}
          cardHeight={this.props.height}
          size="small"
          color={button.color}
          minHeight={26}
          isButton={button.isButton}
          text={button.text}
        >
          {button.count}
        </Badge>
      );
    });
    return (
      <ButtonWrapper
        cardHeight={this.props.height}
        className={classnames({
          hasFocus: this.props.simulateFocus || this.state.focus
        })}
      >
        {availability}
        <PrimaryButtons
          innerRef={x => (this.primaryButtons = x)}
          data-testid="book-card__primary-buttons"
          numPrimaryButtons={this.props.primary.length}
          cardHeight={this.props.height}
        >
          {primary}
          {duration}
        </PrimaryButtons>
        <SecondaryButtons innerRef={x => (this.secondaryButtons = x)}>
          {secondary}
        </SecondaryButtons>
        <Badges cardHeight={this.props.height}>
          {badges}
        </Badges>
      </ButtonWrapper>
    );
  }

  renderSkeletonBook() {
    const { primary, secondary, badges, title, width, height } = this.props;
    return (
      <SkeletonCoverOuter width={width} height={height}>
        <OffScreenComponent>
          {title.decodeHTML()}
          {[...primary, ...secondary, ...badges].map(button => (
            <button key={button.label} onClick={button.onClick}>
              {button.label}
            </button>
          ))}
        </OffScreenComponent>
      </SkeletonCoverOuter>
    );
  }

  renderTitle() {
    const { title, ancillaries } = this.props;
    const normalTitle = (
      <TextEllipsis toolTipText={title.decodeHTML()} lines={2}>
        <div>{title.decodeHTML()}</div>
      </TextEllipsis>
    );
    const prependedTitle = (
      <TextEllipsis toolTipText={title.decodeHTML()} lines={2}>
        <div>
          <PrependText>{t("eresources.courseware", "Courseware")}</PrependText>{" "}
          {title.decodeHTML()}
        </div>
      </TextEllipsis>
    );

    return (
      <Title cardHeight={this.props.height} data-testid="book-card__title">
        {ancillaries ? prependedTitle : normalTitle}
      </Title>
    );
  }

  renderExtraOverlay() {
    const { extraOverlay, width, height, overlayIcon } = this.props;
    if (extraOverlay) {
      return (
        <BookCoverIconOverlay
          data-testid="book-card__icon-overlay"
          width={width}
          height={computeImageHeight(height)}
        >
          {overlayIcon}
        </BookCoverIconOverlay>
      );
    }
  }

  renderHeroBook() {
    const { type, isbn, width, height, title } = this.props;
    return (
      <HeroCard raised>
        <BookCoverOuter
          hero
          width={width}
          height={height - 36}
          onTouchStart={() =>
            this.setState({
              clickable: false
            })
          }
          onTouchEnd={() =>
            setTimeout(() => {
              this.setState({ clickable: true });
            }, 250)
          }
          className={classnames({ hasFocus: this.state.focus })}
        >
          <div className="library-book-cover-outer">
            <div className="library-book-cover-inner">
              <Cover
                bookTitle={title}
                isbn={isbn}
                type={type}
                width={width}
                height={null}
              />
              {this.renderExtraOverlay()}
            </div>
          </div>
          {this.renderButtons()}
        </BookCoverOuter>
      </HeroCard>
    );
  }

  renderBook() {
    const {
      type,
      isbn,
      width,
      height,
      underline,
      simulateFocus,
      title
    } = this.props;
    const coverHeight = underline ? height + 3 : height;

    return (
      <Card raised data-testid="book-card">
        <BookCoverOuter
          width={width}
          height={coverHeight}
          underline={underline}
          onTouchStart={() =>
            this.setState({
              clickable: false
            })
          }
          onTouchEnd={() =>
            setTimeout(() => {
              this.setState({ clickable: true });
            }, 250)
          }
          onClick={this.clickCover}
          className={classnames({
            hasFocus: simulateFocus || this.state.focus
          })}
          data-testid="book-card__outer-cover"
        >
          {this.renderTitle()}
          <div className="library-book-cover-outer">
            <div className="library-book-cover-inner">
              <Cover
                isbn={isbn}
                type={type}
                width={width}
                height={computeImageHeight(height)}
                bookTitle={title}
              />
              {this.renderExtraOverlay()}
            </div>
          </div>
          {this.renderButtons()}
        </BookCoverOuter>
      </Card>
    );
  }

  render() {
    if (!this.state.fullRender) {
      return this.renderSkeletonBook();
    } else if (this.props.hero) {
      return this.renderHeroBook();
    } else {
      return this.renderBook();
    }
  }
}

export default withTheme(Book);

Book.defaultProps = {
  primary: [],
  secondary: [],
  badges: [],
  durationMessage: {},
  availabilityMessage: {},
  width: BASE_CARD_WIDTH,
  height: BASE_CARD_HEIGHT,
  skeleton: false
};

Book.propTypes = {
  primary: PropTypes.array,
  secondary: PropTypes.array,
  badges: PropTypes.array,
  durationMessage: PropTypes.object,
  availabilityMessage: PropTypes.object,
  title: PropTypes.string.isRequired,
  isbn: PropTypes.string.isRequired,
  skeleton: PropTypes.bool
};
